import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'

export interface InboundConnectorPermissions {
    canView: boolean
    canManage: boolean
    canExecute: boolean
    canApproveSync: boolean
}

/**
 * Permission gate for the InboundConnectorDetail page and its embedded
 * SyncRunsTab. Returned shape is stable across 603.03 / 603.04: 603.04's
 * SyncRunsTab imports this hook directly to gate the paused-sync approve /
 * reject buttons via {@code canApproveSync}.
 *
 * - {@code canView}        — read access to detail / sync runs.
 * - {@code canManage}      — destructive + lifecycle controls (enable, disable, delete, configuration edit).
 * - {@code canExecute}     — operational controls (trigger sync, run preview, test connection).
 * - {@code canApproveSync} — paused-sync approve / reject. Maps to the same
 *                            backend permission as {@code manage} today;
 *                            isolated as its own field so 603.04 can swap
 *                            it later without touching call sites.
 *
 * The hook is a thin pure-derived view over {@link useAuth}; it has no I/O
 * and no internal state, which keeps unit-testing trivial.
 */
export function useInboundConnectorPermissions(): InboundConnectorPermissions {
    const {hasPermission} = useAuth()
    return {
        canView: hasPermission(permissions.inboundConnectors.view),
        canManage: hasPermission(permissions.inboundConnectors.manage),
        canExecute: hasPermission(permissions.inboundConnectors.execute),
        canApproveSync: hasPermission(permissions.inboundConnectors.manage),
    }
}
